import * as React from "react";
import { useParams, Link } from "react-router-dom";
import { v4 } from "uuid";
import { useCurrentStudyConfigurationContext } from "../../../Core/CurrentStudyConfigurationContext";
import LoadingWithMessage from "../../../misc/components/LoadingWithMessage";
import { Form, useFormApi, useFormState, Checkbox } from "informed";
import Alert, { AlertCategoryEnum } from "../../../misc/components/Alert";
import { TextBox } from "../../../forms/informedv2/TextBox";
import { IntegerValidator, MinMaxValidator } from "../../../forms/validation/NumericValidators";
import { TextBoxWithStaticSuffix } from "../../../forms/informedv2/TextBoxWithStaticSuffix";
import { CoughSettingsEvents } from "./CoughSettingsEvents";
import { CoughEventTypeItemState } from "./CoughEventType";
import { AuditForm, AuditModal } from "../../../auditmodal/components/AuditModal";
import { ActionButtons } from "../../../misc/ActionButtons";
import { useCookieRedirect } from "../../../misc/CookieRedirect";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../../misc/api/WebPortalTokenClient";
import { TagsInput } from "react-tag-input-component";
import { Tags } from "../../../forms/informedv2/Tags";
import { useReferenceContextProvider } from "../../../Core/ReferenceContextProvider";
import { MultiSelect } from "../../../forms/informedv2/MultiSelect";
import { CoughProcessingAlgorithms, StudyVisualReviewMandatorySetting } from "../../../misc/api/apiTypes";

interface CoughSettingsScreenFormState {
    muteButton: boolean;
    multipleRecordings: boolean;
    visitSelectionEnabled: boolean;
    visualReviewMandatoryQc: boolean;
    visualReviewMandatoryProjectQa: boolean;
    visualReviewMandatoryMonitoringQa: boolean;
    visualReviewMandatoryDifficultCougherQa: boolean;
    visualReviewMandatoryWeeklyQa: boolean;
    sdCardStorage: boolean;
    displayRandomisationColumn: boolean;
    recordingDuration: number;
    minRecordingDuration: number;
    additionalTags: string;
    eventTypes: CoughEventTypeItemState[];
    refEventTypeId?: string;
    processingAlgorithms: CoughProcessingAlgorithms[];
    boutWindowDuration?: number;
    eventDuplicateThreshold?: number;
}

interface CoughSettingsScreenFormProps {
    studyId: string;
    processingAlgorithms: CoughProcessingAlgorithms[];
}

export const DeviceButtonOptions = [{
    Text: "None",
    Value: 0
}, {
    Text: "Button 1",
    Value: 1
}, {
    Text: "Button 2",
    Value: 2
}, {
    Text: "Button 3",
    Value: 3
}, {
    Text: "Button 4 / Mute",
    Value: 4
}]

const StandardEventTypes: CoughEventTypeItemState[] = [
    { id: v4(), order: 12, name: "Removal Subject", description: "", period: true, color: "#0000ff", hidden: false, counted: false, deviceButton: undefined },
    { id: v4(), order: 13, name: "Removal Clinic", description: "", period: true, color: "#ff00ff", hidden: false, counted: false, deviceButton: undefined },
    { id: v4(), order: 14, name: "Early Termination", description: "", period: false, color: "#ff8000", hidden: false, counted: false, deviceButton: undefined },
    { id: v4(), order: 15, name: "Early Stoppage", description: "", period: false, color: "#ffff00", hidden: false, counted: false, deviceButton: undefined },
    { id: v4(), order: 16, name: "Late Attachment", description: "", period: false, color: "#8000ff", hidden: false, counted: false, deviceButton: undefined },
    { id: v4(), order: 17, name: "QA Duration Event", description: "Used for marking QA durations", period: false, color: "#8b4513", hidden: false, counted: false, deviceButton: undefined },
]

export const CoughSettingsScreenForm = ({ studyId, processingAlgorithms }: CoughSettingsScreenFormProps) => {
    const formApi = useFormApi<CoughSettingsScreenFormState>();
    const formState = useFormState<CoughSettingsScreenFormState>();
    const [alertShown, setAlertShown] = React.useState(false);
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const [submitting, setSubmitting] = React.useState(false);
    const [auditModalVisible, setAuditModalVisible] = React.useState(false);
    const { refreshSettings } = useCurrentStudyConfigurationContext();
    const { coughEventTypes } = useReferenceContextProvider();
    const legacyClient = useWebPortalLegacyClient();
    const client = useWebPortalApiClient();

    const onPreAuditSubmit = () => {
        setAlertShown(false);
        setErrorAlert(null);
        setSubmitting(true);
        formApi.validate();

        const state = formApi.getState();

        if (state.invalid) {
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        setSubmitting(false);
        setAuditModalVisible(true);
    }

    const onSubmit = async (auditForm: AuditForm) => {
        setSubmitting(true);
        setAuditModalVisible(false);
        setErrorAlert(null);

        formApi.validate();
        const state = formApi.getState();

        if (state.invalid) {
            setSubmitting(false);
            window.scrollTo(0, 0);
            return setErrorAlert("Errors exist on the page");
        }

        const visualReviewMandatorySettings: StudyVisualReviewMandatorySetting = {
            VisualReviewMandatoryDifficultCougherQa : state.values.visualReviewMandatoryDifficultCougherQa,
            VisualReviewMandatoryMonitoringQa: state.values.visualReviewMandatoryMonitoringQa,
            VisualReviewMandatoryProjectQa: state.values.visualReviewMandatoryProjectQa,
            VisualReviewMandatoryWeeklyQa: state.values.visualReviewMandatoryWeeklyQa,
            VisualReviewMandatoryQc: state.values.visualReviewMandatoryQc
        }

        const payload = JSON.stringify(visualReviewMandatorySettings);

        try {
            await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Cough.RecordingDurationSetting", state.values.recordingDuration);
            await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Cough.MuteEnabledSetting", state.values.muteButton);
            await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Cough.MultipleRecordingsEnabledSetting", state.values.multipleRecordings);
            await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Cough.VisualReviewMandatorySetting", payload);
            await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Cough.VisitSelectionSetting", state.values.visitSelectionEnabled);
            await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Cough.IsSecureDigitalCardSelectedForStorageSetting", state.values.sdCardStorage);
            await legacyClient.putStudySettingArray(studyId, "Vitalograph.WebPortal.Data.Settings.Cough.CoughMarshalRecordingTagsSetting", Array.isArray(state.values.additionalTags) ? state.values.additionalTags : state.values.additionalTags?.split(","));
            await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Cough.RandomisationColumnVisibilitySetting", state.values.displayRandomisationColumn);
            await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Cough.CoughProcessingAlgorithmSetting", state.values.processingAlgorithms.map(e => e.Id));
            await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Cough.TimeBasedBoutWindowDurationSetting", state.values.boutWindowDuration);
            await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Cough.EventDuplicateWarningThresholdSetting", state.values.eventDuplicateThreshold);

            if (state.values.eventTypes == undefined)
                state.values.eventTypes = [];

            const deviceButtonEvents = [
                state.values.eventTypes.some(et => et.deviceButton?.Value === 1),
                state.values.eventTypes.some(et => et.deviceButton?.Value === 2),
                state.values.eventTypes.some(et => et.deviceButton?.Value === 3),
                state.values.eventTypes.some(et => et.deviceButton?.Value === 4),
            ];

            await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Cough.VitaloJakButtonsEnabledSetting", deviceButtonEvents.toString());
            const result = await legacyClient.putStudySetting(studyId, "Vitalograph.WebPortal.Data.Settings.Cough.MinimumRecordingDurationSetting", state.values.minRecordingDuration);

            const currentEventTypes = await client.client.getStudyCoughEventTypes(undefined, `studyId eq ${studyId}`);

            for (const eventType of state.values.eventTypes) {
                const eventTypeModel = {
                    id: eventType.id,
                    studyId: studyId,
                    name: eventType.name,
                    colour: eventType.color,
                    description: eventType.description,
                    order: eventType.order,
                    hidden: eventType.hidden,
                    counted: eventType.counted,
                    period: eventType.period,
                    deviceButton: eventType.deviceButton?.Value,
                    refEventTypeId: eventType.refEventTypeId,
                };

                if (currentEventTypes.value.find(cet => cet.id === eventType.id)) {
                    await client.client.patchStudyCoughEventType(eventTypeModel, eventType.id, auditForm.Annotation);
                } else {
                    await client.client.postStudyCoughEventType(eventTypeModel);
                }
            }

            const toRemove = currentEventTypes.value.filter(et => !state.values.eventTypes.map(e => e.id).includes(et.id));

            for (const eventType of toRemove) {
                await client.client.deleteStudyCoughEventType(eventType.id);
            }

            if (result) {
                setAlertShown(true);
                refreshSettings();
            }
        } catch (ex) {
            setErrorAlert(ex.message);
        }

        window.scrollTo(0, 0)
        setSubmitting(false);
    }

    const recordingDurationValidation = (value: number) => {
        return IntegerValidator(value) || MinMaxValidator(value, 1, 48);
    }

    const minRecordingDurationValidation = (value: number) => {
        return IntegerValidator(value) || MinMaxValidator(value, 0, 100);
    }

    return (
        <>
            <Alert show={alertShown} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/studies`}>Studies list</Link>?
            </Alert>
            <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            <div className="row">
                <div className="col-md-12">
                    <h2 className="text-primary">Cough Settings</h2>
                    <div className="panel panel-default">
                        <div className="panel-heading">
                            <h3 className="panel-title">Session Configuration</h3>
                        </div>
                        <div className="panel-body">
                            <div className="row">
                                <div className="form-group col-md-4">
                                    <label>Mute Enabled</label>
                                    <p className="help-block">Allows the user to mute the VitaloJAK microphone. When enabled this will override the button 4 functionality (VitaloJAK 2 Only)</p>
                                    <Checkbox<boolean, CoughSettingsScreenFormState> field="muteButton" />
                                </div>
                                <div className="form-group col-md-4">
                                    <label>Multiple Recordings</label>
                                    <p className="help-block">Determines whether multiple recordings can be set up on the VitaloJAK (VitaloJAK 2 Only)</p>
                                    <Checkbox<boolean, CoughSettingsScreenFormState> field="multipleRecordings" />
                                </div>
                            
                                <div className="form-group col-md-4">
                                    <label>Visit Selection</label>
                                    <p className="help-block">Allows the user to select sessions from a list on the device (VitaloJAK 2 Only)</p>
                                    <Checkbox<boolean, CoughSettingsScreenFormState> field="visitSelectionEnabled" />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-md-4">
                                    <label>SD Card Storage</label>
                                    <p className="help-block">Determines if the VitaloJAK uses an external SD card, or if it uses its internal memory (VitaloJAK 2 Only)</p>
                                    <Checkbox<boolean, CoughSettingsScreenFormState> field="sdCardStorage" />
                                </div>
                                <div className="form-group col-md-4">
                                    <label>Display Randomisation Column</label>
                                    <p className="help-block">Displays the subject&apos;s randomisation number in the list of cough sessions.</p>
                                    <Checkbox<boolean, CoughSettingsScreenFormState> field="displayRandomisationColumn" />
                                </div>
                                <div className="form-group col-md-4">
                                    <label>Cough Duplicate Threshold (milliseconds)</label>
                                    <p className="help-block">The threshold between an existing cough event in milliseconds that a warning will be displayed to the analyst when marking a new cough event</p>
                                    <TextBoxWithStaticSuffix<number, CoughSettingsScreenFormState> id="eventDuplicateThreshold" field="eventDuplicateThreshold" staticpart="ms" type="number" min="0" />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-md-4">
                                    <label>Recording Duration (hours)</label>
                                    <p className="help-block">The length of the cough recording, in hours. Minimum of 1 hour and a maximum of 48 hours (VitaloJAK 2 Only)</p>
                                    <TextBoxWithStaticSuffix<number, CoughSettingsScreenFormState> id="recordingDuration" field="recordingDuration" staticpart="hours" type="number" min="1" max="48" required validate={recordingDurationValidation} />
                                </div>
                                <div className="form-group col-md-4">
                                    <label>Minimum Recording Duration</label>
                                    <p className="help-block">The minimum duration a cough recording is allowed to be, as a percentage of the total recording duration</p>
                                    <TextBoxWithStaticSuffix<number, CoughSettingsScreenFormState> id="minRecordingDuration" field="minRecordingDuration" staticpart="%" type="number" min="0" max="100" required validate={minRecordingDurationValidation} />
                                </div>
                                <div className="form-group col-md-4">
                                    <label>Additional Tags</label>
                                    <p className="help-block">A list of additional identification tags to send with each cough recording on this study to Cough Marshal</p>
                                    <Tags field="additionalTags" />
                                </div>
                            </div>
                            <div className="row">
                                <div className="form-group col-md-4">
                                    <label>Require review on Analysis Set Types</label>
                                    <p className="help-block">Makes the Review step mandatory on the following Analysis Sets when enabled</p>
                                    <p><Checkbox<boolean, CoughSettingsScreenFormState> field="visualReviewMandatoryQc" /> <label>QC Analysis Set</label></p>
                                    <p><Checkbox<boolean, CoughSettingsScreenFormState> field="visualReviewMandatoryProjectQa" /> <label>Project QA Analysis Set</label></p>
                                    <p><Checkbox<boolean, CoughSettingsScreenFormState> field="visualReviewMandatoryMonitoringQa" /> <label>Monitoring QA Analysis Set</label></p>
                                    <p><Checkbox<boolean, CoughSettingsScreenFormState> field="visualReviewMandatoryDifficultCougherQa" /> <label>Difficult Cougher QA Analysis Set</label></p>
                                    <p><Checkbox<boolean, CoughSettingsScreenFormState> field="visualReviewMandatoryWeeklyQa" /> <label>Weekly QA Analysis Set</label></p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="panel panel-default">
                        <div className="panel-heading">
                            <h3 className="panel-title">Algorithm Configuration</h3>
                        </div>
                        <div className="panel-body">
                            <div className="row">
                                <div className="form-group col-md-4">
                                    <label>Processing Algorithms</label>
                                    <p className="help-block">The processing algorithm to apply to the cough session</p>
                                    <MultiSelect
                                        id="processingAlgorithms"
                                        field="processingAlgorithms"
                                        options={processingAlgorithms}
                                        multi={true}
                                        labelKey="Name"
                                        valueKey="Id"
                                    />
                                </div>
                                {formState.values.processingAlgorithms && formState.values.processingAlgorithms.map(x => x.Id).includes(3) ? <div className="form-group col-md-4">
                                    <label>Bout Window Duration (seconds)</label>
                                    <p className="help-block">The length of the window to consider a cough peak part of a bout</p>
                                    <TextBoxWithStaticSuffix<number, CoughSettingsScreenFormState> id="boutWindowDuration" field="boutWindowDuration" staticpart="seconds" type="number" />
                                </div> : null}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <CoughSettingsEvents />
            <div className="row">
                <div className="col-md-12">
                    <ActionButtons submitting={submitting} onSaveClick={() => onPreAuditSubmit()} automationIdPrefix="ActionsHeader" returnToListRoute="/studies" />
                </div>
            </div>
            {auditModalVisible ? (
                <AuditModal
                    header="Approval"
                    isSubmitting={submitting}
                    onClose={() => setAuditModalVisible(false)}
                    onSubmit={(audit) => onSubmit(audit)}
                />
            ) : null}
        </>
    );
}

export const CoughSettings = () => {
    const { id } = useParams<{ id: string }>();
    const [initialValues, setInitialValues] = React.useState<CoughSettingsScreenFormState>();
    const client = useWebPortalApiClient();
    const [processingAlgorithms, setProcessingAlgorithms] = React.useState<CoughProcessingAlgorithms[]>([]);

    const legacyClient = useWebPortalLegacyClient();

    useCookieRedirect(id);

    React.useEffect(() => {
        const getSettings = async () => {
            const coughSettings = await legacyClient.getStudyCoughSettings(id);
            const algorithms = await legacyClient.getCoughProcessingAlgorithms();
            setProcessingAlgorithms(algorithms);

            let eventTypeResult: CoughEventTypeItemState[] = (await client.client.getStudyCoughEventTypes(undefined, `studyId eq ${id}`, "order asc")).value.map(e => ({
                id: e.id,
                name: e.name,
                order: e.order,
                description: e.description,
                color: e.colour,
                period: e.period,
                hidden: e.hidden,
                counted: e.counted,
                deviceButton: DeviceButtonOptions.find(d => d.Value === e.deviceButton),
                refEventTypeId: e.refEventTypeId
            }));

            if (eventTypeResult.length === 0) {
                const coughEventTypesResult = await client.client.getCoughEventTypes();
                const mappedServerEvents: CoughEventTypeItemState[] = coughEventTypesResult.value.map(x => ({
                    id: v4(), order: 0, name: x.name, description: x.description, period: x.isPeriod, color: x.defaultColor, hidden: false, counted: true, deviceButton: undefined, refEventTypeId: x.id,
                }));

                eventTypeResult = [...mappedServerEvents, ...StandardEventTypes];
            }

            setInitialValues({
                muteButton: coughSettings.MuteEnabled,
                minRecordingDuration: coughSettings.MinimumRecordingDuration,
                recordingDuration: coughSettings.RecordingDuration,
                additionalTags: coughSettings.RecordingTags,
                multipleRecordings: coughSettings.MultipleRecordingsEnabled,
                sdCardStorage: coughSettings.IsSecureDigitalCard,
                displayRandomisationColumn: coughSettings.DisplayRandomisationColumn,
                visitSelectionEnabled: coughSettings.VisitSelection,
                eventTypes: eventTypeResult,
                processingAlgorithms: coughSettings.ProcessingAlgorithms.map(e => algorithms.find(x => x.Id == e)),
                boutWindowDuration: coughSettings.BoutWindowDuration,
                eventDuplicateThreshold: coughSettings.EventDuplicateThreshold,
                visualReviewMandatoryDifficultCougherQa: coughSettings.VisualReviewMandatorySetting.VisualReviewMandatoryDifficultCougherQa,
                visualReviewMandatoryMonitoringQa: coughSettings.VisualReviewMandatorySetting.VisualReviewMandatoryMonitoringQa,
                visualReviewMandatoryProjectQa: coughSettings.VisualReviewMandatorySetting.VisualReviewMandatoryProjectQa,
                visualReviewMandatoryQc: coughSettings.VisualReviewMandatorySetting.VisualReviewMandatoryQc,
                visualReviewMandatoryWeeklyQa: coughSettings.VisualReviewMandatorySetting.VisualReviewMandatoryWeeklyQa

            });
        }

        getSettings();
    }, [client.client, id]);

    if (!initialValues) {
        return <LoadingWithMessage message="Loading Cough Settings..." />;
    }

    return (
        <Form noValidate initialValues={initialValues}>
            <CoughSettingsScreenForm studyId={id} processingAlgorithms={processingAlgorithms} />
        </Form>
    );
}
